/**
 * Collection et recherche : filtres et tri instantanés (Section Rendering API),
 * tiroir de filtres sur mobile. Sans JavaScript, le formulaire reste utilisable.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.drawer = this.querySelector('[data-facets-drawer]');

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (target.matches('[data-sort-mirror]')) {
        const sort = this.querySelector('[data-sort]');
        if (sort) sort.value = target.value;
      }
      if (target.closest('[data-facets-form]') || target.matches('[data-sort], [data-sort-mirror]')) {
        clearTimeout(this.debounce);
        const delay = target.type === 'number' ? 600 : 0;
        this.debounce = setTimeout(() => this.applyForm(), delay);
      }
    });

    this.addEventListener('submit', (event) => {
      event.preventDefault();
      this.applyForm();
    });

    this.addEventListener('click', (event) => {
      const link = event.target.closest('[data-facets-link]');
      if (link) {
        event.preventDefault();
        this.render(new URL(link.href, window.location.origin).searchParams, link.closest('.pagination'));
        return;
      }
      if (event.target.closest('[data-facets-open]')) this.toggleDrawer(true);
      if (event.target.closest('[data-facets-close]')) this.toggleDrawer(false);
    });

    window.addEventListener('popstate', () => this.render(new URLSearchParams(window.location.search), false, false));
  }

  toggleDrawer(open) {
    if (!this.drawer) return;
    this.drawer.classList.toggle('is-open', open);
    window.themeLockScroll?.(open && window.matchMedia('(max-width: 989px)').matches);
    if (open) this.drawer.querySelector('summary, input, select')?.focus({ preventScroll: true });
    else this.querySelector('[data-facets-open]')?.focus({ preventScroll: true });
  }

  applyForm() {
    const form = this.querySelector('[data-facets-form]');
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (value !== '') params.append(key, value);
    });
    const sort = this.querySelector('[data-sort]');
    if (sort) params.set('sort_by', sort.value);
    this.render(params);
  }

  async render(params, scrollToTop = false, push = true) {
    params.delete('section_id');
    const query = params.toString();
    const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
    this.classList.add('is-loading');

    try {
      const html = await fetch(`${url}${query ? '&' : '?'}section_id=${this.sectionId}`).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('collection-filters');
      if (!fresh) throw new Error('Section introuvable');

      // On garde l'état ouvert/fermé des groupes de filtres
      const openGroups = new Set(
        [...this.querySelectorAll('.facets__group[open]')].map((el) => el.dataset.facetIndex)
      );
      const replace = (selector) => {
        const current = this.querySelector(selector);
        const next = fresh.querySelector(selector);
        if (current && next) current.innerHTML = next.innerHTML;
      };
      replace('[data-results]');
      replace('[data-facets-body]');
      replace('[data-results-count]');
      replace('[data-results-count-label]');
      this.querySelectorAll('.facets__group').forEach((el) => {
        if (openGroups.has(el.dataset.facetIndex)) el.open = true;
      });
      this.querySelectorAll('[data-results] [data-reveal]').forEach((el) => el.classList.add('is-revealed'));

      if (push) window.history.pushState({}, '', url);
      if (scrollToTop) this.querySelector('.collection__toolbar')?.scrollIntoView({ behavior: 'smooth' });
    } catch (error) {
      window.location.href = url;
    } finally {
      this.classList.remove('is-loading');
    }
  }
}
customElements.define('collection-filters', CollectionFilters);
